import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Tooltip, Typography, alpha } from '@mui/material';
import { AccessTime } from '@mui/icons-material';

import { HEADER_HEIGHT } from '@frontend/features/common/constants/layout';
import type { FareCountdown } from '../useFareCountdown';

/**
 * Alto FIJO de la barra. Es fijo a propósito: la barra de filtros se pega
 * justo debajo y necesita saber cuánto bajar sin medir el DOM. Por eso el
 * resumen es UNA línea recortada y no un bloque que crece con el vertical.
 */
export const ACTIVE_SEARCH_SUMMARY_HEIGHT = 52;

interface Props {
  label: string;
  /** Los datos de la búsqueda comprometida; se muestran en una sola línea. */
  lines: string[];
  /** Vigencia de la generación de resultados que se está mostrando. */
  countdown?: FareCountdown;
  /** Volver a pedir precios cuando la vigencia se agota. */
  onRefresh?: () => void;
}

/**
 * El contrato de la lista: QUÉ búsqueda produjo estos resultados. Queda pegada
 * arriba mientras se scrollea, porque a la tercera card ya no se recuerda si
 * la fecha era el 13 o el 14.
 *
 * La vigencia de los precios vive ACÁ y no en cada card: es propiedad de la
 * BÚSQUEDA, no de cada oferta. En una card se leería como escasez ("quedan
 * pocos") y no es eso — es que la cotización envejece.
 */
export function ActiveSearchSummary({ label, lines, countdown, onRefresh }: Props) {
  const { t } = useTranslation();

  if (lines.length === 0) return null;

  const summary = lines.filter(Boolean).join(' · ');
  const stale = countdown?.status === 'expiring' || countdown?.status === 'expired';
  const expired = countdown?.status === 'expired';
  // Cerca del vencimiento el botón deja de ser "editar" y pasa a ser lo único
  // que sirve: volver a pedir precios.
  const refreshing = Boolean(onRefresh && stale);
  const validityText = expired
    ? t('travelServices.resultsExpiredShort')
    : t('travelServices.pricesValidFor', { time: countdown?.label ?? '' });

  return (
    <Paper
      variant="outlined"
      sx={(theme) => {
        const tint = alpha(theme.palette.primary.main, 0.05);
        return {
          position: 'sticky',
          top: HEADER_HEIGHT,
          zIndex: 3,
          height: ACTIVE_SEARCH_SUMMARY_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          borderRadius: 1,
          borderColor: alpha(theme.palette.primary.main, 0.18),
          // Opaca: es una barra pegada y los resultados pasan por debajo.
          backgroundColor: theme.palette.background.paper,
          backgroundImage: `linear-gradient(${tint}, ${tint})`,
        };
      }}
    >
      {/* El bloque de la izquierda es el que ABSORBE el achique: se recorta
          con puntos suspensivos en vez de empujar la barra fuera de la
          pantalla. En viewports chicos el rótulo sobra — la barra ya se
          entiende por su contenido. */}
      <Box
        minWidth={0}
        flex={1}
        overflow="hidden"
        display="flex"
        alignItems="baseline"
        gap={1}
      >
        <Typography
          variant="caption"
          color="primary"
          fontWeight={700}
          sx={{
            display: { xs: 'none', md: 'block' },
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        <Typography variant="body2" color="text.primary" noWrap title={summary}>
          {summary}
        </Typography>
      </Box>

      <Box ml="auto" display="flex" alignItems="center" gap={1.5} flexShrink={0}>
        {countdown && countdown.status !== 'unknown' && (
          <Tooltip title={validityText}>
            <Typography
              variant="caption"
              color={stale ? 'warning.dark' : 'text.secondary'}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              <AccessTime sx={{ fontSize: 14 }} />
              {/* En pantallas chicas la frase entera no entra al lado del
                  botón: queda el reloj con el tiempo, y la frase completa en
                  el tooltip. */}
              <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' } }}>
                {validityText}
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', lg: 'none' } }}>
                {expired ? '0:00' : countdown.label}
              </Box>
            </Typography>
          </Tooltip>
        )}
        <Button
          size="small"
          variant={refreshing ? 'contained' : 'outlined'}
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => {
            if (refreshing) {
              onRefresh?.();
              return;
            }
            // Editar es volver al formulario, que vive arriba de la lista.
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          {t(
            refreshing
              ? 'travelServices.refreshPrices'
              : 'travelServices.editSearch',
          )}
        </Button>
      </Box>
    </Paper>
  );
}
